import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  Archive,
  ArchiveRestore,
  Bell,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Columns3,
  Crown,
  Download,
  ExternalLink,
  FileText,
  Flame,
  Hand,
  Link as LinkIcon,
  MessageCircle,
  Pencil,
  PhoneCall,
  Plus,
  Search,
  Send,
  Trophy,
  TrendingUp,
  TriangleAlert,
  UserPlus,
  Users,
  UsersRound,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ROLE_PRESETS, LEAD_SCOPES, PERMISSIONS as RBAC_PERMISSIONS, PRESET_DEFAULTS, roleLabel } from "@/lib/rbac";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useAuth } from "@/contexts/AuthContext";
import { API, api, formatApiError } from "@/lib/api";
import AddLeadDialog from "@/components/AddLeadDialog";

import TasksPanel from "@/components/TasksPanel";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import WhatsAppConversation from "@/components/WhatsAppConversation";
import MedaHero from "@/components/MedaHero";
import { NotesTimeline } from "@/components/NotesTimeline";
import EditLeadDetailsModal from "@/components/EditLeadDetailsModal";
import MobileLeadsList from "@/components/MobileLeadsList";
import TeamPresencePill from "@/components/TeamPresencePill";
import MobileFAB from "@/components/MobileFAB";
import AttemptCounter from "@/components/AttemptCounter";
import LeadContactActions from "@/components/LeadContactActions";
import ContactPolicyBadge, { policyFor } from "@/components/ContactPolicyBadge";
import DispositionModal from "@/components/DispositionModal";
import { SLAModuleCard, SLAComplianceSheet } from "@/components/SLAPanel";

const STATUSES = [
  "New",
  "Contacted",
  "Follow-up",
  "Zoom Done",
  "Visited",
  "Docs Recd",
  "Enrolled",
  "Closed-lost",
  "Wrong number",
];
const STATUS_STYLES = {
  New: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  Contacted: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  "Follow-up": "bg-indigo-100 text-indigo-800 hover:bg-indigo-100",
  "Zoom Done": "bg-violet-100 text-violet-800 hover:bg-violet-100",
  Visited: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  "Docs Recd": "bg-teal-100 text-teal-800 hover:bg-teal-100",
  Enrolled: "bg-green-100 text-green-800 hover:bg-green-100",
  "Closed-lost": "bg-slate-100 text-slate-400 hover:bg-slate-100",
  "Wrong number": "bg-rose-100 text-rose-700 hover:bg-rose-100",
  // Legacy rows
  Closed: "bg-green-100 text-green-800 hover:bg-green-100",
};
const TEST_STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-700",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
};
const TIER_STYLES = {
  Platinum: "bg-[#0A2540] text-white",
  Gold: "bg-amber-100 text-amber-900",
  Silver: "bg-slate-200 text-slate-800",
  Bronze: "bg-orange-100 text-orange-900",
  None: "bg-slate-100 text-slate-500",
};

const TEMPERATURE_STYLES = {
  Hot: "bg-rose-100 text-rose-800 hover:bg-rose-100",
  Warm: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  Cool: "bg-sky-100 text-sky-800 hover:bg-sky-100",
  Cold: "bg-slate-100 text-slate-600 hover:bg-slate-100",
};

const PARENT_OPTIONS = [
  { v: "high", l: "High — both parents involved" },
  { v: "medium", l: "Medium — one parent involved" },
  { v: "low", l: "Low — student decides alone" },
];

const BUDGET_OPTIONS = [
  { v: "above_20l", l: "₹20L+ / year" },
  { v: "10_20l", l: "₹10–20L / year" },
  { v: "5_10l", l: "₹5–10L / year" },
  { v: "under_5l", l: "Under ₹5L / year" },
];

const ALL_COLUMNS = [
  { k: "student", l: "Student", default: true, locked: true },
  { k: "contact", l: "Contact", default: true },
  { k: "actions", l: "Actions", default: true },
  { k: "policy", l: "Policy", default: true },
  { k: "neet", l: "NEET", default: false },
  { k: "country", l: "Country", default: false },
  { k: "test", l: "Test", default: false },
  { k: "test_score", l: "Test Score", default: false },
  { k: "score", l: "Lead Score", default: true },
  { k: "sla", l: "SLA", default: true },
  { k: "slab", l: "Slab", default: true },
  { k: "source", l: "Source", default: false },
  { k: "owner", l: "Owner", default: true },
  { k: "status", l: "Status", default: true },
  { k: "last_note", l: "Last note", default: true },
  { k: "created", l: "Created", default: true },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}
function fmtINR(n) {
  if (n == null) return "—";
  if (n >= 100000) return `₹${(n / 100000).toFixed(2).replace(/\.00$/, "")}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [sendingResult, setSendingResult] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappData, setWhatsappData] = useState(null);
  const [experiment, setExperiment] = useState(null);
  const [nudges, setNudges] = useState({ nudges: [], counts: {}, total: 0 });
  const [sendingNudgeFor, setSendingNudgeFor] = useState(null);
  const [scoreboard, setScoreboard] = useState(null);
  const [scorePeriod, setScorePeriod] = useState("all");
  const [teamOpen, setTeamOpen] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [newCounsellor, setNewCounsellor] = useState({
    name: "", email: "", password: "",
    access_role: "counsellor",
    lead_scope: PRESET_DEFAULTS.counsellor.scope,
    permissions: PRESET_DEFAULTS.counsellor.perms,
    pipeline_restrict: "",
  });
  const [teamPipelines, setTeamPipelines] = useState([]);
  const [creatingCounsellor, setCreatingCounsellor] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [tasksSummary, setTasksSummary] = useState({ total: 0, counts: {} });
  const [slaSummary, setSlaSummary] = useState(null);
  const [slaOpen, setSlaOpen] = useState(false);
  const [phaseA, setPhaseA] = useState(null);
  const [viewFilter, setViewFilter] = useState("active"); // active | archived
  const [dateRange, setDateRange] = useState("today"); // today | 7d | 30d | all
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState("created"); // 'created' | 'score' | 'smart'
  const [overdueOnly, setOverdueOnly] = useState(
    () => searchParams.get("overdue") === "true"
  ); // Rec A: filter table to SLA-breached leads
  const [dispoLead, setDispoLead] = useState(null); // lead awaiting call-outcome modal
  const pendingCallRef = useRef(null); // {lead, startedAt}
  const [columnVis, setColumnVis] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("medix_cols") || "null");
      if (saved && typeof saved === "object") return saved;
    } catch {
      /* parse error — ignore */
    }
    return Object.fromEntries(ALL_COLUMNS.map((c) => [c.k, c.default]));
  });
  useEffect(() => {
    localStorage.setItem("medix_cols", JSON.stringify(columnVis));
  }, [columnVis]);
  const isColVisible = (k) => columnVis[k] !== false;

  const isAdmin = user?.role === "admin";
  const winnerCelebratedRef = useRef(false);

  // Daily winner celebration: fire when current user is #1 in today's scoreboard.
  useEffect(() => {
    if (!user || winnerCelebratedRef.current) return;
    const todayKey = `medix_winner_${user.id}_${new Date().toISOString().slice(0, 10)}`;
    if (localStorage.getItem(todayKey)) {
      winnerCelebratedRef.current = true;
      return;
    }
    let cancelled = false;
    api
      .get("/admin/scoreboard", { params: { period: "today" } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = data?.rows || [];
        const top = rows.find((r) => r.score > 0);
        if (top && top.id === user.id) {
          winnerCelebratedRef.current = true;
          localStorage.setItem(todayKey, "1");
          // Confetti burst
          try {
            confetti({
              particleCount: 90,
              spread: 70,
              origin: { y: 0.25 },
              colors: ["#C9A961", "#0A2540", "#34D399", "#FBBF24"],
            });
            setTimeout(
              () =>
                confetti({
                  particleCount: 60,
                  angle: 60,
                  spread: 55,
                  origin: { x: 0.05, y: 0.4 },
                }),
              250,
            );
            setTimeout(
              () =>
                confetti({
                  particleCount: 60,
                  angle: 120,
                  spread: 55,
                  origin: { x: 0.95, y: 0.4 },
                }),
              500,
            );
          } catch {
            /* confetti is best-effort */
          }
          toast.success(
            `You're #1 today, ${user.name?.split(" ")[0] || "champ"}! Score ${top.score}`,
            {
              description:
                "Top of the leaderboard. Keep closing leads and ticking off tasks.",
              duration: 6000,
            },
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const topCounsellor = useMemo(() => {
    const rows = scoreboard?.rows || [];
    const top = rows.find((r) => r.score > 0);
    return top || null;
  }, [scoreboard]);

  // Disposition modal trigger: when user dialled from inline icon and returns to tab,
  // prompt them to log the call outcome. Single-shot per dial.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const pending = pendingCallRef.current;
      if (!pending) return;
      const elapsed = Date.now() - pending.startedAt;
      // Ignore quick tab-switches (<5s) — counsellor probably didn't actually call
      if (elapsed < 5000) return;
      setDispoLead(pending.lead);
      pendingCallRef.current = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const onInlineCall = useCallback((lead) => {
    pendingCallRef.current = { lead, startedAt: Date.now() };
  }, []);

  const applyLeadUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    setLeads((prev) =>
      prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)),
    );
    setSelected((prev) =>
      prev && prev.id === updated.id ? { ...prev, ...updated } : prev,
    );
  }, []);

  // Re-render every minute so "X min ago" labels stay fresh
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((x) => x + 1), 60000);
    return () => clearInterval(t);
  }, []);


  const abWinnerLabel = useMemo(() => {
    if (!experiment) return null;
    if (experiment.winner === "A" || experiment.winner === "B") {
      return `${experiment.winner} winning`;
    }
    if (experiment.winner === "tie") return "Even split";
    const totalImps =
      (experiment.variants?.A?.impressions || 0) +
      (experiment.variants?.B?.impressions || 0);
    if (totalImps === 0) return null;
    return "Collecting…";
  }, [experiment]);

  const loadAll = useCallback(async () => {
    try {
      const sinceIso = (() => {
        const now = new Date();
        if (dateRange === "today") {
          const d = new Date(now);
          d.setHours(0, 0, 0, 0);
          return d.toISOString();
        }
        if (dateRange === "7d") return new Date(now.getTime() - 7 * 86400000).toISOString();
        if (dateRange === "30d") return new Date(now.getTime() - 30 * 86400000).toISOString();
        return null; // all-time
      })();
      const statsParams = sinceIso ? { since: sinceIso } : {};
      const [s, l, exp, nud, sb, tk, sla, pa] = await Promise.all([
        api.get("/admin/stats", { params: statsParams }),
        api.get("/admin/leads", {
          params: {
            ...(statusFilter !== "all" && { status_filter: statusFilter }),
            ...(sortBy === "score" && { sort: "score" }),
            ...(sortBy === "smart" && { sort: "smart" }),
            archived: viewFilter === "archived" ? "true" : "false",
          },
        }),
        api.get("/admin/experiments/cta").catch(() => ({ data: null })),
        api.get("/admin/nudges", { params: statsParams }).catch(() => ({ data: { nudges: [], counts: {}, total: 0 } })),
        api.get("/admin/scoreboard", { params: { period: scorePeriod } }).catch(() => ({ data: null })),
        api.get("/admin/tasks", { params: { scope: "mine", status: "open" } }).catch(() => ({ data: { total: 0, counts: {} } })),
        api.get("/admin/sla/today").catch(() => ({ data: null })),
        api.get("/admin/dashboard/phase-a").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setLeads(l.data.leads);
      setExperiment(exp.data);
      setNudges(nud.data);
      setScoreboard(sb.data);
      setTasksSummary({ total: tk.data?.total || 0, counts: tk.data?.counts || {} });
      setSlaSummary(sla.data);
      setPhaseA(pa.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [statusFilter, scorePeriod, sortBy, viewFilter, dateRange]);

  const openArchiveDialog = useCallback(() => {
    setArchiveReason("");
    setArchiveOpen(true);
  }, []);

  const submitArchive = useCallback(async () => {
    if (!selected) return;
    const reason = archiveReason.trim();
    if (!reason) {
      toast.error("Tell us why you're archiving — helps audit later");
      return;
    }
    setArchiveSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/leads/${selected.id}/archive`,
        { reason },
      );
      toast.success("Lead archived");
      applyLeadUpdate(data.lead);
      setSelected(data.lead);
      setArchiveOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setArchiveSubmitting(false);
    }
  }, [selected, archiveReason, applyLeadUpdate, loadAll]);

  const restoreLead = useCallback(
    async (lead) => {
      try {
        const { data } = await api.post(`/admin/leads/${lead.id}/restore`);
        toast.success("Lead restored to active pipeline");
        applyLeadUpdate(data.lead);
        loadAll();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    },
    [applyLeadUpdate, loadAll],
  );

  const loadCounsellors = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/counsellors");
      setCounsellors(data.counsellors);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 15000);
    return () => clearInterval(t);
  }, [loadAll]);

  // Keep URL ?overdue=true in sync with state (Rec A: deep-linkable filter)
  const applyOverdueFilter = useCallback(
    (next) => {
      setOverdueOnly(next);
      const params = new URLSearchParams(searchParams);
      if (next) params.set("overdue", "true");
      else params.delete("overdue");
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = leads;
    if (overdueOnly) {
      rows = rows.filter((l) => policyFor(l).kind === "overdue");
      // Sort hottest + oldest breach first
      const tempRank = { Hot: 4, Warm: 3, Cool: 2, Cold: 1 };
      rows = [...rows].sort((a, b) => {
        const ra = tempRank[a.lead_temperature] || 1;
        const rb = tempRank[b.lead_temperature] || 1;
        if (rb !== ra) return rb - ra;
        const ha = a.attempt_status?.hours_behind || 0;
        const hb = b.attempt_status?.hours_behind || 0;
        return hb - ha;
      });
    }
    if (!q) return rows;
    return rows.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.phone.toLowerCase().includes(q) ||
        (l.preferred_country || "").toLowerCase().includes(q),
    );
  }, [leads, search, overdueOnly]);

  const openLead = async (lead) => {
    setSelected(lead);
    setNotesDraft("");
    setNotesRefreshKey((k) => k + 1);
    setReport(null);
    try {
      const { data } = await api.get(`/admin/leads/${lead.id}/test-report`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateLead = async (leadId, patch) => {
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, patch);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? data.lead : l)));
      if (selected?.id === leadId) setSelected(data.lead);
      return data.lead;
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const onChangeStatus = async (leadId, status) => {
    await updateLead(leadId, { status });
    toast.success("Status updated");
  };

  const onSaveNotes = async () => {
    if (!selected) return;
    const content = notesDraft.trim();
    if (!content) {
      toast.error("Note is empty");
      return;
    }
    setSavingNotes(true);
    try {
      const { data: note } = await api.post(
        `/admin/leads/${selected.id}/notes`,
        { content },
      );
      // Mirror the new latest_note onto local lead state so the table column updates immediately.
      const latestNote = {
        preview: content.slice(0, 140),
        source: "manual",
        created_at: note.created_at,
        created_by_name: note.created_by_name,
      };
      setLeads((prev) =>
        prev.map((l) =>
          l.id === selected.id
            ? { ...l, latest_note: latestNote, notes_count: (l.notes_count || 0) + 1 }
            : l,
        ),
      );
      setSelected((s) =>
        s ? { ...s, latest_note: latestNote, notes_count: (s.notes_count || 0) + 1 } : s,
      );
      setNotesDraft("");
      setNotesRefreshKey((k) => k + 1);
      toast.success("Note saved to timeline");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNotes(false);
    }
  };

  const sendResult = async () => {
    if (!selected) return;
    setSendingResult(true);
    try {
      const { data } = await api.post(
        `/admin/leads/${selected.id}/whatsapp-result`,
      );
      setWhatsappData(data);
      setWhatsappOpen(true);
      setSelected({
        ...selected,
        last_result_sent_at: data.sent_at,
        last_result_channel: "whatsapp",
      });
      setLeads((prev) =>
        prev.map((l) =>
          l.id === selected.id
            ? {
                ...l,
                last_result_sent_at: data.sent_at,
                last_result_channel: "whatsapp",
              }
            : l,
        ),
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingResult(false);
    }
  };

  const sendNudge = async (lead_id, nudge_type) => {
    setSendingNudgeFor(lead_id);
    try {
      const { data } = await api.post(`/admin/leads/${lead_id}/nudge`, {
        nudge_type,
      });
      setWhatsappData({ ...data, channel: "whatsapp", nudge: true });
      setSelected(
        leads.find((l) => l.id === lead_id) ||
          nudges.nudges.find((n) => n.lead_id === lead_id),
      );
      setWhatsappOpen(true);
      // Refresh nudge list after action (lead now in cooldown)
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingNudgeFor(null);
    }
  };

  const claimLead = async (lead, e) => {
    e?.stopPropagation();
    setClaiming(lead.id);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/claim`);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? data.lead : l)));
      if (selected?.id === lead.id) setSelected(data.lead);
      toast.success(`Claimed by ${data.lead.assigned_to_name}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClaiming(null);
    }
  };

  const releaseLead = async (lead, e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/release`);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? data.lead : l)));
      if (selected?.id === lead.id) setSelected(data.lead);
      toast.success("Lead released");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openTeamPanel = async () => {
    await loadCounsellors();
    try {
      const r = await api.get("/admin/pipelines");
      setTeamPipelines(r.data?.pipelines || []);
    } catch { setTeamPipelines([]); }
    setTeamOpen(true);
  };

  const applyRolePreset = (key) => {
    const d = PRESET_DEFAULTS[key] || PRESET_DEFAULTS.counsellor;
    setNewCounsellor((c) => ({ ...c, access_role: key, lead_scope: d.scope, permissions: [...d.perms] }));
  };

  const toggleNewPerm = (perm) => {
    setNewCounsellor((c) => ({
      ...c,
      permissions: c.permissions.includes(perm)
        ? c.permissions.filter((p) => p !== perm)
        : [...c.permissions, perm],
    }));
  };

  const createCounsellor = async () => {
    if (!newCounsellor.name || !newCounsellor.email || !newCounsellor.password) {
      toast.error("Fill name, email and password");
      return;
    }
    if (newCounsellor.password.length < 8) {
      toast.error("Password must be ≥ 8 chars");
      return;
    }
    if (newCounsellor.lead_scope === "pipeline" && !newCounsellor.pipeline_restrict) {
      toast.error("Pick a pipeline to restrict to");
      return;
    }
    setCreatingCounsellor(true);
    try {
      await api.post("/admin/counsellors", {
        name: newCounsellor.name,
        email: newCounsellor.email,
        password: newCounsellor.password,
        access_role: newCounsellor.access_role,
        lead_scope: newCounsellor.lead_scope,
        permissions: newCounsellor.permissions,
        pipeline_restrict: newCounsellor.lead_scope === "pipeline" ? newCounsellor.pipeline_restrict : null,
      });
      toast.success("Team member added");
      setNewCounsellor({
        name: "", email: "", password: "",
        access_role: "counsellor",
        lead_scope: PRESET_DEFAULTS.counsellor.scope,
        permissions: PRESET_DEFAULTS.counsellor.perms,
        pipeline_restrict: "",
      });
      loadCounsellors();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingCounsellor(false);
    }
  };

  const toggleCounsellor = async (c) => {
    try {
      await api.patch(`/admin/counsellors/${c.id}`, { is_active: !c.is_active });
      loadCounsellors();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const previewPdf = async () => {    if (!selected) return;
    try {
      const token = localStorage.getItem("medix_admin_token");
      const res = await fetch(
        `${API}/admin/leads/${selected.id}/result-pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not preview PDF");
    }
  };

  const openSchedule = () => {
    if (!selected) return;
    if (selected.scheduled_call_at) {
      const d = new Date(selected.scheduled_call_at);
      setScheduleDate(d.toISOString().slice(0, 10));
      setScheduleTime(d.toTimeString().slice(0, 5));
    } else {
      const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
      setScheduleDate(t.toISOString().slice(0, 10));
      setScheduleTime("11:00");
    }
    setScheduleNote(selected.scheduled_call_note || "");
    setScheduleOpen(true);
  };

  const submitSchedule = async () => {
    if (!selected) return;
    if (!scheduleDate || !scheduleTime) {
      toast.error("Pick a date and time");
      return;
    }
    setScheduleSaving(true);
    try {
      const localDate = new Date(`${scheduleDate}T${scheduleTime}:00`);
      const iso = localDate.toISOString();
      const { data } = await api.post(`/admin/leads/${selected.id}/schedule-call`, {
        scheduled_at: iso,
        note: scheduleNote,
      });
      setSelected(data.lead);
      setLeads((prev) => prev.map((l) => (l.id === data.lead.id ? data.lead : l)));
      toast.success("Counsellor call scheduled");
      setScheduleOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScheduleSaving(false);
    }
  };

  const downloadScorecard = async (lead) => {
    try {
      const token = localStorage.getItem("medix_admin_token");
      const res = await fetch(`${API}/admin/leads/${lead.id}/scorecard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const ext = lead.scorecard_filename?.split(".").pop() || "bin";
      const link = document.createElement("a");
      link.href = url;
      link.download = lead.scorecard_filename || `scorecard-${lead.id}.${ext}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download scorecard");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 pl-16 pr-14 sm:px-6 sm:pr-14 md:pl-6 lg:px-10 lg:pr-16">
          <div>
            <div className="font-display text-base font-bold tracking-tight text-[#0A2540]">
              Pipeline
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Lead &amp; Scholarship Management
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && <TeamPresencePill />}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2"
                onClick={openTeamPanel}
                data-testid="open-team-panel"
              >
                <Users className="h-4 w-4" /> Team
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-10">
        <MedaHero />
        {/* Date range filter — controls Stats, Nudges, Activity, Analytics */}
        <div
          className="mb-3 flex flex-wrap items-center gap-2"
          data-testid="dashboard-date-range"
        >
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
            Showing data from
          </span>
          {[
            { k: "today", l: "Today" },
            { k: "7d", l: "Last 7 days" },
            { k: "30d", l: "Last 30 days" },
            { k: "all", l: "All time" },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setDateRange(opt.k)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                dateRange === opt.k
                  ? "border-[#0A2540] bg-[#0A2540] text-white"
                  : "border-slate-300 bg-white text-slate-600 hover:border-[#0A2540]"
              }`}
              data-testid={`date-range-${opt.k}`}
            >
              {opt.l}
            </button>
          ))}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={UsersRound} label="Total Leads" value={stats?.total_leads ?? "—"} sub={`${stats?.new_leads ?? 0} new`} testid="stat-total" />
          <StatCard icon={CheckCircle2} label="Tests Completed" value={stats?.tests_completed ?? "—"} sub={stats?.total_leads ? `${Math.round((100 * stats.tests_completed) / Math.max(1, stats.total_leads))}% completion` : "Run rate"} testid="stat-completed" />
          <StatCard icon={TrendingUp} label="Avg Score" value={stats?.avg_score ?? "—"} sub="out of 18" testid="stat-avg" />
          <StatCard icon={FileText} label="Enrolled" value={stats?.closed_leads ?? "—"} sub="Status = Enrolled / won" testid="stat-closed" />
        </div>

        {/* Phase-A operational cards — Connect Rate · Overdue · Personal Links */}
        <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="phase-a-row">
          <ConnectRateCard data={phaseA?.connect_rate} />
          <OverduePipelineCard
            data={phaseA?.overdue_pipeline}
            onOpenLead={(leadId) => {
              const lead = leads.find((x) => x.id === leadId);
              if (lead) openLead(lead);
            }}
            onApplyFilter={() => {
              applyOverdueFilter(true);
              setStatusFilter("all");
              // Scroll to leads table
              setTimeout(() => {
                document
                  .querySelector('[data-testid="leads-search"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
            onResolved={loadAll}
          />
          <PersonalLinksCard data={phaseA?.personal_links} />
        </div>

        {/* Module strip — compact summary cards open side-drawers */}
        {(topCounsellor || nudges.total > 0 || abWinnerLabel || tasksSummary.total > 0 || slaSummary?.total_leads > 0) && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="module-strip">
            {isAdmin && slaSummary?.total_leads > 0 && (
              <SLAModuleCard data={slaSummary} onClick={() => setSlaOpen(true)} />
            )}
            {tasksSummary.total > 0 && (
              <button
                onClick={() => setTasksOpen(true)}
                className="group flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50/40 px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-violet-400 hover:shadow-md"
                data-testid="module-card-tasks"
              >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-violet-500 text-white">
                  <ClipboardList className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-violet-700">
                    My tasks
                  </div>
                  <div className="font-display truncate text-base font-semibold text-[#0A2540]">
                    {tasksSummary.total} open
                    {tasksSummary.counts?.overdue > 0 && (
                      <span className="ml-1 text-rose-600">
                        · {tasksSummary.counts.overdue} overdue
                      </span>
                    )}
                  </div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 flex-none text-slate-300 transition-colors group-hover:text-[#0A2540]" />
              </button>
            )}
            {topCounsellor && (
              <button
                onClick={() => setOpenModule("scoreboard")}
                className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#C9A961] hover:shadow-md"
                data-testid="module-card-scoreboard"
              >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-[#C9A961]/15 text-[#C9A961]">
                  <Trophy className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Top counsellor
                  </div>
                  <div className="font-display truncate text-base font-semibold text-[#0A2540]">
                    {topCounsellor.name}{" "}
                    <span className="text-slate-400">· {topCounsellor.score}</span>
                  </div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 flex-none text-slate-300 transition-colors group-hover:text-[#0A2540]" />
              </button>
            )}
            {nudges.total > 0 && (
              <button
                onClick={() => setOpenModule("nudges")}
                className="group flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/40 px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md"
                data-testid="module-card-nudges"
              >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-amber-500 text-white">
                  <Bell className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700">
                    Recovery queue
                  </div>
                  <div className="font-display truncate text-base font-semibold text-[#0A2540]">
                    {nudges.total} nudge{nudges.total === 1 ? "" : "s"} ready
                  </div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 flex-none text-slate-300 transition-colors group-hover:text-[#0A2540]" />
              </button>
            )}
            {abWinnerLabel && (
              <button
                onClick={() => setOpenModule("experiment")}
                className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#0A2540] hover:shadow-md"
                data-testid="module-card-experiment"
              >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-[#0A2540]/10 text-[#0A2540]">
                  <Zap className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Hero CTA test
                  </div>
                  <div className="font-display truncate text-base font-semibold text-[#0A2540]">
                    Variant {abWinnerLabel}
                  </div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 flex-none text-slate-300 transition-colors group-hover:text-[#0A2540]" />
              </button>
            )}
          </div>
        )}

        {/* Counsellor Scoreboard — side drawer */}
        {scoreboard && scoreboard.rows && scoreboard.rows.length > 0 && (
          <Sheet
            open={openModule === "scoreboard"}
            onOpenChange={(v) => !v && setOpenModule(null)}
          >
            <SheetContent
              className="w-full overflow-y-auto sm:max-w-3xl"
              data-testid="scoreboard-panel"
            >
              <SheetHeader>
                <SheetTitle className="font-display flex items-center gap-2 tracking-tight">
                  <Trophy className="h-5 w-5 text-[#C9A961]" />
                  Counsellor scoreboard
                </SheetTitle>
                <SheetDescription>
                  Who&rsquo;s moving the funnel?
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex items-center gap-1 rounded-md border border-slate-200 bg-[#F8F9FA] p-1 text-xs">
                {[
                  { k: "today", l: "Today" },
                  { k: "week", l: "Week" },
                  { k: "month", l: "Month" },
                  { k: "all", l: "All time" },
                ].map((p) => (
                  <button
                    key={p.k}
                    onClick={() => setScorePeriod(p.k)}
                    className={`rounded px-3 py-1.5 transition-colors ${
                      scorePeriod === p.k
                        ? "bg-[#0A2540] text-white"
                        : "text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid={`score-period-${p.k}`}
                  >
                    {p.l}
                  </button>
                ))}
              </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    <th className="py-2 text-left">#</th>
                    <th className="py-2 text-left">Counsellor</th>
                    <th className="py-2 text-right">Claimed</th>
                    <th className="py-2 text-right">Calls</th>
                    <th className="py-2 text-right">WA Results</th>
                    <th className="py-2 text-right">Nudges</th>
                    <th className="py-2 text-right">Tasks ✓</th>
                    <th className="py-2 text-right">Closed</th>
                    <th className="py-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {scoreboard.rows.map((r, i) => (
                    <tr
                      key={r.id}
                      className={`border-b border-slate-100 ${
                        !r.is_active ? "opacity-50" : ""
                      } ${i === 0 ? "bg-[#FBF6E8]" : ""}`}
                      data-testid={`scoreboard-row-${r.id}`}
                    >
                      <td className="py-3 align-middle">
                        <div className="flex items-center gap-1.5">
                          {i === 0 ? (
                            <Crown className="h-4 w-4 text-[#C9A961]" fill="#C9A961" />
                          ) : (
                            <span className="font-mono-clock text-xs text-slate-500">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="font-semibold text-[#0A2540]">{r.name}</div>
                        <div className="text-xs text-slate-500">
                          {r.email}
                          <span className="ml-1 text-[10px] uppercase tracking-wider text-[#C9A961]">
                            · {r.role}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-right font-mono-clock text-[#0A2540]">
                        {r.leads_claimed}
                      </td>
                      <td className="py-3 text-right font-mono-clock text-[#0A2540]">
                        {r.calls_scheduled}
                      </td>
                      <td className="py-3 text-right font-mono-clock text-[#0A2540]">
                        {r.whatsapp_results}
                      </td>
                      <td className="py-3 text-right font-mono-clock text-[#0A2540]">
                        {r.nudges_sent}
                      </td>
                      <td className="py-3 text-right font-mono-clock text-violet-700">
                        {r.tasks_completed ?? 0}
                      </td>
                      <td className="py-3 text-right font-mono-clock font-semibold text-emerald-700">
                        {r.deals_closed}
                      </td>
                      <td className="py-3 text-right">
                        <span
                          className={`inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded-md px-2 font-mono-clock text-sm font-bold ${
                            i === 0
                              ? "bg-[#0A2540] text-white"
                              : "bg-slate-100 text-[#0A2540]"
                          }`}
                        >
                          {r.score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Score = claimed×1 + calls×2 + wa-results×3 + nudges×1 + tasks-done×0.5 + closed×5
            </div>
            </SheetContent>
          </Sheet>
        )}

        {/* WhatsApp Nudges panel — side drawer */}
        {nudges.total > 0 && (
          <Sheet
            open={openModule === "nudges"}
            onOpenChange={(v) => !v && setOpenModule(null)}
          >
            <SheetContent
              className="w-full overflow-y-auto sm:max-w-2xl"
              data-testid="nudges-panel"
            >
              <SheetHeader>
                <SheetTitle className="font-display flex items-center gap-2 tracking-tight">
                  <Bell className="h-5 w-5 text-amber-500" />
                  Recovery queue · {nudges.total} ready
                </SheetTitle>
                <SheetDescription>
                  WhatsApp nudges for abandoned funnels — 24-hour cooldown per lead.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 flex flex-wrap gap-2 text-xs">
                {[
                  { k: "pending", label: "Abandoned register", color: "bg-blue-100 text-blue-800" },
                  { k: "timeout", label: "Quiz timed out", color: "bg-rose-100 text-rose-800" },
                  { k: "result_unsent", label: "Result unsent", color: "bg-emerald-100 text-emerald-800" },
                ].map((c) => (
                  <Badge
                    key={c.k}
                    className={`${c.color} hover:${c.color}`}
                    data-testid={`nudge-count-${c.k}`}
                  >
                    {c.label}: {nudges.counts?.[c.k] || 0}
                  </Badge>
                ))}
              </div>

              <div className="mt-4 grid gap-2">
              {nudges.nudges.slice(0, 8).map((n) => {
                const typeStyle =
                  n.nudge_type === "pending"
                    ? "bg-blue-100 text-blue-800"
                    : n.nudge_type === "timeout"
                      ? "bg-rose-100 text-rose-800"
                      : "bg-emerald-100 text-emerald-800";
                const typeLabel =
                  n.nudge_type === "pending"
                    ? "Abandoned register"
                    : n.nudge_type === "timeout"
                      ? "Quiz timed out"
                      : "Result unsent";
                return (
                  <div
                    key={n.lead_id}
                    className="flex flex-col gap-2 rounded-lg border border-amber-200/70 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`nudge-row-${n.lead_id}`}
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-3">
                      <Badge className={`${typeStyle} hover:${typeStyle} font-medium`}>
                        {typeLabel}
                      </Badge>
                      <div>
                        <div className="text-sm font-semibold text-[#0A2540]">{n.name}</div>
                        <div className="text-xs text-slate-500">
                          {n.phone} · {n.preferred_country}
                          {n.scholarship_tier && ` · ${n.scholarship_tier}`}
                        </div>
                      </div>
                      <div className="ml-auto text-right text-xs text-slate-500 sm:ml-0">
                        <span className="font-mono-clock">{n.hours_since_event}h</span> ago
                        {n.nudge_count > 0 && (
                          <span className="ml-2 text-amber-700">
                            · nudged {n.nudge_count}×
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => sendNudge(n.lead_id, n.nudge_type)}
                      disabled={sendingNudgeFor === n.lead_id}
                      className="h-9 gap-1.5 bg-[#25D366] text-white hover:bg-[#1EBE5C] sm:w-auto"
                      data-testid={`nudge-send-${n.lead_id}`}
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      {sendingNudgeFor === n.lead_id ? "Preparing…" : "Send Nudge"}
                    </Button>
                  </div>
                );
              })}
              {nudges.total > 8 && (
                <div className="pt-1 text-center text-xs text-slate-500">
                  + {nudges.total - 8} more in the queue. Sending one moves it
                  into a 24h cooldown.
                </div>
              )}
            </div>
            </SheetContent>
          </Sheet>
        )}

        {/* A/B experiment panel — side drawer */}
        {experiment && abWinnerLabel && (
          <Sheet
            open={openModule === "experiment"}
            onOpenChange={(v) => !v && setOpenModule(null)}
          >
            <SheetContent
              className="w-full overflow-y-auto sm:max-w-2xl"
              data-testid="ab-panel"
            >
              <SheetHeader>
                <SheetTitle className="font-display flex items-center gap-2 tracking-tight">
                  <Zap className="h-5 w-5 text-[#0A2540]" />
                  Hero CTA experiment
                </SheetTitle>
                <SheetDescription>
                  Which copy converts visitors into leads?
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 text-xs">
                {experiment.winner ? (
                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                    {experiment.winner === "tie"
                      ? "Even split — keep collecting"
                      : `Winner: Variant ${experiment.winner}`}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-300 text-slate-500">
                    Need ≥10 impressions per variant
                  </Badge>
                )}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {["A", "B"].map((v) => {
                  const d = experiment.variants[v];
                  const isWinner = experiment.winner === v;
                  return (
                    <div
                      key={v}
                      className={`rounded-lg border p-4 ${
                        isWinner
                          ? "border-emerald-300 bg-emerald-50/40"
                          : "border-slate-200 bg-[#F8F9FA]"
                      }`}
                      data-testid={`ab-variant-${v}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                          Variant {v}
                        </div>
                        {isWinner && (
                          <Badge className="bg-emerald-200 text-emerald-900 hover:bg-emerald-200">
                            Winning
                          </Badge>
                        )}
                      </div>
                      <div className="font-display mt-1 text-sm font-semibold text-[#0A2540]">
                        &ldquo;{d.label}&rdquo;
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <KV label="Impressions" value={d.impressions} />
                        <KV label="Registered" value={d.registrations} />
                        <KV label="Reg rate" value={`${d.register_rate}%`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Filters — desktop only */}
        <div className="mt-8 hidden md:flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="font-display text-xl font-semibold text-[#0A2540]">
              Leads
            </div>
            <Badge variant="outline" className="border-slate-300 text-slate-600" data-testid="leads-count">
              {filtered.length} shown
            </Badge>
            {overdueOnly && (
              <button
                type="button"
                onClick={() => applyOverdueFilter(false)}
                data-testid="overdue-filter-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 transition"
                title="Click to remove this filter"
              >
                <TriangleAlert className="h-3 w-3" />
                Overdue only
                <span className="text-rose-500">✕</span>
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search name, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 w-72 pl-9"
                data-testid="leads-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 w-36" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={viewFilter} onValueChange={setViewFilter}>
                <SelectTrigger className="h-10 w-32" data-testid="view-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="view-filter-active">
                    Active
                  </SelectItem>
                  <SelectItem value="archived" data-testid="view-filter-archived">
                    Archived
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-10 w-40" data-testid="sort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="smart">Smart (SLA first)</SelectItem>
                <SelectItem value="created">Newest first</SelectItem>
                <SelectItem value="score">Lead score (hot →)</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 gap-2"
                  data-testid="columns-toggle"
                >
                  <Columns3 className="h-4 w-4" /> Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ALL_COLUMNS.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.k}
                    checked={columnVis[c.k] !== false}
                    onCheckedChange={(v) =>
                      setColumnVis((prev) => ({ ...prev, [c.k]: v }))
                    }
                    disabled={c.locked}
                    data-testid={`col-toggle-${c.k}`}
                  >
                    {c.l}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              className="h-10 gap-2 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              onClick={() => setAddLeadOpen(true)}
              data-testid="open-add-lead"
            >
              <Plus className="h-4 w-4" /> Add Lead
            </Button>
          </div>
        </div>

        {/* Mobile leads list — phones/tablets */}
        <div className="mt-6 pb-24 md:hidden">
          <MobileLeadsList
            leads={leads}
            userId={user?.id}
            onSelect={(l) => setSelected(l)}
          />
        </div>

        {/* Table — desktop only */}
        <div className="mt-4 hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1F5F9]">
                {isColVisible("student") && <TableHead>Student</TableHead>}
                {isColVisible("contact") && <TableHead>Contact</TableHead>}
                {isColVisible("actions") && <TableHead>Actions</TableHead>}
                {isColVisible("policy") && <TableHead>Policy</TableHead>}
                {isColVisible("neet") && <TableHead>NEET</TableHead>}
                {isColVisible("country") && <TableHead>Country</TableHead>}
                {isColVisible("test") && <TableHead>Test</TableHead>}
                {isColVisible("test_score") && <TableHead>Test Score</TableHead>}
                {isColVisible("score") && <TableHead>Lead Score</TableHead>}
                {isColVisible("sla") && <TableHead>SLA</TableHead>}
                {isColVisible("slab") && <TableHead>Slab</TableHead>}
                {isColVisible("source") && <TableHead>Source</TableHead>}
                {isColVisible("owner") && <TableHead>Owner</TableHead>}
                {isColVisible("status") && <TableHead>Status</TableHead>}
                {isColVisible("last_note") && <TableHead>Last note</TableHead>}
                {isColVisible("created") && <TableHead className="text-right">Created</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={Object.values(columnVis).filter((v) => v !== false).length}
                    className="py-16 text-center text-sm text-slate-500"
                  >
                    No leads yet. Share your landing page to start collecting registrations.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((l) => (
                <TableRow
                  key={l.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openLead(l)}
                  data-testid={`lead-row-${l.id}`}
                >
                  {isColVisible("student") && (
                  <TableCell>
                    <div className="font-semibold text-[#0A2540]">{l.name}</div>
                    {l.scorecard_path && (
                      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-emerald-700">
                        Scorecard ✓
                      </div>
                    )}
                  </TableCell>
                  )}
                  {isColVisible("contact") && (
                  <TableCell>
                    <div className="text-sm text-slate-700">{l.email}</div>
                    <div className="text-xs text-slate-500">{l.phone}</div>
                  </TableCell>
                  )}
                  {isColVisible("actions") && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <LeadContactActions
                      lead={l}
                      disabled={policyFor(l).kind === "closed"}
                      onAttemptLogged={(channel) => {
                        // Optimistic update so the dot/tooltip refresh immediately
                        const iso = new Date().toISOString();
                        setLeads((prev) =>
                          prev.map((x) =>
                            x.id === l.id
                              ? {
                                  ...x,
                                  last_contacted_at: iso,
                                  attempts: {
                                    ...(x.attempts || {}),
                                    last_at: {
                                      ...(x.attempts?.last_at || {}),
                                      [channel]: iso,
                                    },
                                    last_channel: channel,
                                  },
                                }
                              : x,
                          ),
                        );
                      }}
                      onAfterCall={() => onInlineCall(l)}
                    />
                  </TableCell>
                  )}
                  {isColVisible("policy") && (
                  <TableCell>
                    <ContactPolicyBadge lead={l} />
                    {(() => {
                      const p = policyFor(l);
                      if (p.kind !== "overdue") return null;
                      const sev = p.severity || "low";
                      const dot =
                        sev === "high"
                          ? "bg-rose-500"
                          : sev === "med"
                            ? "bg-amber-500"
                            : "bg-slate-400";
                      return (
                        <div
                          className="mt-1 flex items-start gap-1.5 text-[10.5px] text-rose-700 leading-snug max-w-[200px]"
                          data-testid={`breach-reason-${l.id}`}
                          title={p.reason}
                        >
                          <span
                            className={`mt-1 inline-block h-1.5 w-1.5 rounded-full shrink-0 ${dot}`}
                          />
                          <span className="line-clamp-2">{p.reason}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  )}
                  {isColVisible("neet") && (
                  <TableCell>
                    <div className="font-mono-clock text-sm text-[#0A2540]">{l.neet_score}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                      {l.neet_score_type}
                    </div>
                  </TableCell>
                  )}
                  {isColVisible("country") && (
                    <TableCell className="text-sm text-slate-700">{l.preferred_country}</TableCell>
                  )}
                  {isColVisible("test") && (
                  <TableCell>
                    <Badge className={`${TEST_STATUS_STYLES[l.test_status]} font-medium`} variant="secondary">
                      {l.test_status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  )}
                  {isColVisible("test_score") && (
                  <TableCell>
                    {l.test_score != null ? (
                      <span className="font-mono-clock text-sm font-semibold text-[#0A2540]">
                        {l.test_score}/{l.test_total}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  )}
                  {isColVisible("score") && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`${TEMPERATURE_STYLES[l.lead_temperature || "Cold"]} font-medium`}
                        data-testid={`lead-temp-${l.id}`}
                      >
                        {l.lead_temperature === "Hot" && (
                          <Flame className="mr-1 h-3 w-3" fill="currentColor" />
                        )}
                        {l.lead_temperature || "Cold"}
                      </Badge>
                      <span className="font-mono-clock text-xs text-slate-500">
                        {l.lead_score ?? 0}
                      </span>
                    </div>
                  </TableCell>
                  )}
                  {isColVisible("sla") && (
                  <TableCell>
                    <AttemptCounter status={l.attempt_status} variant="compact" />
                  </TableCell>
                  )}
                  {isColVisible("slab") && (
                  <TableCell>
                    {l.scholarship_tier ? (
                      <div>
                        <Badge className={`${TIER_STYLES[l.scholarship_tier] || "bg-slate-100 text-slate-700"} font-medium`}>
                          {l.scholarship_tier}
                        </Badge>
                        <div className="mt-0.5 font-mono-clock text-[11px] text-slate-500">
                          {fmtINR(l.scholarship_amount)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </TableCell>
                  )}
                  {isColVisible("source") && (
                  <TableCell>
                    {l.utm_source ? (
                      <div className="text-xs">
                        <div className="font-medium text-[#0A2540]">{l.utm_source}</div>
                        <div className="text-slate-500">
                          {[l.utm_medium, l.utm_campaign].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">direct</span>
                    )}
                  </TableCell>
                  )}
                  {isColVisible("owner") && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {l.assigned_to ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#0A2540]/10 text-[10px] font-bold text-[#0A2540]"
                          title={l.assigned_to_name || ""}
                        >
                          {(l.assigned_to_name || "?")
                            .split(" ")
                            .map((p) => p[0])
                            .slice(0, 2)
                            .join("")
                            .toUpperCase()}
                        </span>
                        <div className="text-xs">
                          <div className="font-medium text-[#0A2540]">
                            {l.assigned_to_name}
                          </div>
                          {(isAdmin || l.assigned_to === user?.id) && (
                            <button
                              onClick={(e) => releaseLead(l, e)}
                              className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-rose-600"
                              data-testid={`release-${l.id}`}
                            >
                              release
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => claimLead(l, e)}
                        disabled={claiming === l.id}
                        className="h-7 gap-1 px-2 text-xs"
                        data-testid={`claim-${l.id}`}
                      >
                        <Hand className="h-3 w-3" />
                        {claiming === l.id ? "…" : "Claim"}
                      </Button>
                    )}
                  </TableCell>
                  )}
                  {isColVisible("status") && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={l.status} onValueChange={(v) => onChangeStatus(l.id, v)}>
                      <SelectTrigger
                        className={`h-8 w-32 border-0 ${STATUS_STYLES[l.status] || ""}`}
                        data-testid={`row-status-${l.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  )}
                  {isColVisible("last_note") && (
                    <TableCell
                      className="max-w-[200px] align-top"
                      data-testid={`row-last-note-${l.id}`}
                    >
                      {l.latest_note ? (
                        <div className="text-xs leading-snug">
                          <div
                            className="line-clamp-2 text-slate-700"
                            title={l.latest_note.preview}
                          >
                            {l.latest_note.source === "voice_transcript" && (
                              <span className="mr-1 inline-block rounded-sm bg-violet-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-800">
                                🎙
                              </span>
                            )}
                            {l.latest_note.preview}
                          </div>
                          <div className="mt-0.5 text-[10px] text-slate-400">
                            {l.latest_note.created_by_name || "—"} ·{" "}
                            {formatDate(l.latest_note.created_at)}
                          </div>
                        </div>
                      ) : l.notes ? (
                        <div
                          className="line-clamp-2 text-xs italic text-slate-500"
                          title={l.notes}
                        >
                          {l.notes}
                        </div>
                      ) : (
                        <span className="text-xs italic text-slate-300">—</span>
                      )}
                    </TableCell>
                  )}
                  {isColVisible("created") && (
                    <TableCell className="text-right text-xs text-slate-500">{formatDate(l.created_at)}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Detail drawer */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl tracking-tight text-[#0A2540]">
                  <span className="inline-flex items-center gap-2">
                    {selected.name}
                    <button
                      type="button"
                      onClick={() => setEditDetailsOpen(true)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#0A2540]"
                      title="Edit name / phone / email / country"
                      data-testid="drawer-edit-details"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </SheetTitle>
                <div className="text-sm text-slate-500">
                  {selected.email} &middot; {selected.phone}
                </div>
              </SheetHeader>

              {/* Quick action row */}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={sendResult}
                  disabled={sendingResult || selected.test_status !== "completed"}
                  className="h-9 gap-1.5 bg-[#25D366] text-white hover:bg-[#1EBE5C]"
                  data-testid="drawer-send-whatsapp"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  {sendingResult
                    ? "Preparing…"
                    : selected.last_result_sent_at
                      ? "Resend on WhatsApp"
                      : "Send via WhatsApp"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={previewPdf}
                  disabled={selected.test_status !== "completed"}
                  className="h-9 gap-1.5"
                  data-testid="drawer-preview-pdf"
                >
                  <FileText className="h-3.5 w-3.5" /> Preview PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openSchedule}
                  className="h-9 gap-1.5"
                  data-testid="drawer-schedule-call"
                >
                  <CalendarClock className="h-3.5 w-3.5" />
                  {selected.scheduled_call_at ? "Reschedule Call" : "Schedule Call"}
                </Button>
                {selected.scorecard_path && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadScorecard(selected)}
                    className="h-9 gap-1.5"
                    data-testid="download-scorecard"
                  >
                    <Download className="h-3.5 w-3.5" /> Scorecard
                  </Button>
                )}
                {selected.assigned_to ? (
                  (isAdmin || selected.assigned_to === user?.id) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => releaseLead(selected, e)}
                      className="h-9 gap-1.5 text-rose-700"
                      data-testid="drawer-release"
                    >
                      <X className="h-3.5 w-3.5" /> Release
                    </Button>
                  )
                ) : (
                  <Button
                    size="sm"
                    onClick={(e) => claimLead(selected, e)}
                    disabled={claiming === selected.id}
                    className="h-9 gap-1.5 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                    data-testid="drawer-claim"
                  >
                    <Hand className="h-3.5 w-3.5" /> Claim Lead
                  </Button>
                )}
                {isAdmin && (
                  selected.archived ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreLead(selected)}
                      className="h-9 gap-1.5 border-emerald-300 text-emerald-700"
                      data-testid="drawer-restore"
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openArchiveDialog}
                      className="h-9 gap-1.5 border-slate-300 text-slate-600 hover:border-rose-300 hover:text-rose-700"
                      data-testid="drawer-archive"
                    >
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </Button>
                  )
                )}
              </div>

              {selected.archived && (
                <div
                  className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600"
                  data-testid="drawer-archived-banner"
                >
                  <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                    <Archive className="h-3.5 w-3.5" /> Archived
                    {selected.archived_by_name && (
                      <span className="font-normal text-slate-500">
                        · by {selected.archived_by_name}
                      </span>
                    )}
                    {selected.archived_at && (
                      <span className="font-normal text-slate-500">
                        · {formatDate(selected.archived_at)}
                      </span>
                    )}
                  </div>
                  {selected.archive_reason && (
                    <div className="mt-1 text-slate-600">
                      Reason: {selected.archive_reason}
                    </div>
                  )}
                </div>
              )}

              {(selected.last_result_sent_at || selected.scheduled_call_at) && (
                <div className="mt-3 rounded-md border border-slate-200 bg-[#F8F9FA] p-3 text-xs">
                  {selected.last_result_sent_at && (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <MessageCircle className="h-3.5 w-3.5" />
                      Result sent {formatDate(selected.last_result_sent_at)} via{" "}
                      <span className="font-semibold uppercase tracking-wider">
                        {selected.last_result_channel || "whatsapp"}
                      </span>
                    </div>
                  )}
                  {selected.scheduled_call_at && (
                    <div className="mt-1.5 flex items-center gap-2 text-violet-700">
                      <CalendarClock className="h-3.5 w-3.5" />
                      Call: {formatDate(selected.scheduled_call_at)}
                      {selected.scheduled_call_note && (
                        <span className="text-slate-500">— {selected.scheduled_call_note}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Snapshot grid */}
              <div className="mt-5 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-[#F8F9FA] p-4 text-sm">
                <KV label="Country" value={selected.preferred_country} />
                <KV label="NEET" value={`${selected.neet_score} (${selected.neet_score_type})`} />
                <KV
                  label="Qualification"
                  value={formatQualification(selected.qualification)}
                />
                <KV label="Passing year" value={selected.passing_year} />
                <KV
                  label="WhatsApp"
                  value={
                    selected.whatsapp_number &&
                    selected.whatsapp_number !== selected.phone
                      ? selected.whatsapp_number
                      : selected.whatsapp_number
                        ? `${selected.whatsapp_number} (same as phone)`
                        : null
                  }
                />
                <KV
                  label="Best time to call"
                  value={formatCallTime(selected.best_time_to_call)}
                />
                <KV label="Test status" value={selected.test_status.replace("_", " ")} />
                <KV
                  label="Score"
                  value={
                    selected.test_score != null
                      ? `${selected.test_score}/${selected.test_total}${
                          selected.scholarship_percentage != null
                            ? ` · ${selected.scholarship_percentage}%`
                            : ""
                        }`
                      : "—"
                  }
                />
                <KV
                  label="Scholarship"
                  value={
                    selected.scholarship_tier
                      ? `${selected.scholarship_tier} · ${fmtINR(selected.scholarship_amount)}`
                      : "—"
                  }
                />
                <KV label="Source" value={selected.utm_source ? `${selected.utm_source}${selected.utm_medium ? ` / ${selected.utm_medium}` : ""}` : "Direct"} />
                <KV label="Campaign" value={selected.utm_campaign} />
                <KV label="Owner" value={selected.assigned_to_name || "Unclaimed"} />
                <KV label="Created" value={formatDate(selected.created_at)} />
                <KV label="Submitted" value={formatDate(selected.test_completed_at)} />
              </div>

              {/* Status & notes */}
              <div className="mt-6 space-y-4">
                {/* Lead score panel */}
                <div
                  className="rounded-lg border border-slate-200 bg-white p-4"
                  data-testid="lead-score-panel"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      Auto lead score
                    </div>
                    <Badge
                      className={`${TEMPERATURE_STYLES[selected.lead_temperature || "Cold"]} font-medium`}
                    >
                      {selected.lead_temperature === "Hot" && (
                        <Flame className="mr-1 h-3 w-3" fill="currentColor" />
                      )}
                      {selected.lead_temperature || "Cold"} · {selected.lead_score ?? 0}
                    </Badge>
                  </div>
                  {selected.lead_score_breakdown && (
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      {[
                        ["NEET marks", selected.lead_score_breakdown.neet],
                        ["Test perf.", selected.lead_score_breakdown.test],
                        ["Parent involv.", selected.lead_score_breakdown.parent_involvement],
                        ["Budget pot.", selected.lead_score_breakdown.budget_potential],
                      ].map(([label, b]) => {
                        const pct = b?.max ? Math.min(100, (b.points / b.max) * 100) : 0;
                        return (
                          <div key={label} className="rounded-md bg-[#F8F9FA] p-2">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">{label}</span>
                              <span className="font-mono-clock text-[#0A2540]">
                                {b?.points ?? 0}
                                <span className="text-slate-400">/{b?.max ?? 0}</span>
                              </span>
                            </div>
                            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                              <div
                                className="h-full bg-[#0A2540]"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {selected.attempt_status && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        Outreach attempts (SLA)
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-wider ${
                          selected.attempt_status.status === "behind"
                            ? "text-rose-600"
                            : selected.attempt_status.status === "slight"
                              ? "text-amber-600"
                              : "text-emerald-600"
                        }`}
                      >
                        {selected.attempt_status.status === "fresh"
                          ? "Just arrived"
                          : selected.attempt_status.status === "on_track"
                            ? "On track"
                            : selected.attempt_status.status === "slight"
                              ? "Slightly behind"
                              : "Behind schedule"}
                      </span>
                    </div>
                    <AttemptCounter status={selected.attempt_status} variant="full" />
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Parent involvement
                    </Label>
                    <Select
                      value={selected.parent_involvement || ""}
                      onValueChange={async (v) => {
                        const updated = await updateLead(selected.id, { parent_involvement: v });
                        if (updated) toast.success("Score updated");
                      }}
                    >
                      <SelectTrigger
                        className="mt-1.5 h-11"
                        data-testid="drawer-parent-involvement"
                      >
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        {PARENT_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>
                            {o.l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Budget potential
                    </Label>
                    <Select
                      value={selected.budget_band || ""}
                      onValueChange={async (v) => {
                        const updated = await updateLead(selected.id, { budget_band: v });
                        if (updated) toast.success("Score updated");
                      }}
                    >
                      <SelectTrigger
                        className="mt-1.5 h-11"
                        data-testid="drawer-budget-band"
                      >
                        <SelectValue placeholder="Not set" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>
                            {o.l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Status</Label>
                  <Select value={selected.status} onValueChange={(v) => onChangeStatus(selected.id, v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="drawer-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    Walk &amp; Talk
                  </Label>
                  <div className="mt-1.5">
                    <VoiceNoteRecorder
                      leadId={selected.id}
                      onTranscribed={async () => {
                        // Voice-transcript appears as a new timeline entry server-side.
                        // Bump key to force NotesTimeline reload, and refresh the leads
                        // table so the "last note" column updates.
                        setNotesRefreshKey((k) => k + 1);
                        try {
                          const r = await api.get(`/admin/leads`);
                          const list = Array.isArray(r.data)
                            ? r.data
                            : r.data?.leads || [];
                          const fresh = list.find((x) => x.id === selected.id);
                          if (fresh) {
                            setLeads((arr) =>
                              arr.map((x) => (x.id === fresh.id ? fresh : x)),
                            );
                            setSelected(fresh);
                          }
                        } catch {
                          /* noop */
                        }
                      }}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Notes</Label>
                  <Textarea
                    rows={4}
                    placeholder="Type a note. Press Save — every entry stays timestamped in the timeline below."
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="mt-1.5"
                    data-testid="drawer-notes"
                  />
                  <Button
                    size="sm"
                    onClick={onSaveNotes}
                    disabled={savingNotes || !notesDraft.trim()}
                    className="mt-2 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                    data-testid="drawer-save-notes"
                  >
                    {savingNotes ? "Saving…" : "Save notes"}
                  </Button>
                  <NotesTimeline
                    leadId={selected.id}
                    refreshKey={notesRefreshKey}
                  />
                </div>
              </div>

              {/* WhatsApp conversation */}
              <div className="mt-8">
                <h3 className="font-display text-lg font-semibold text-[#0A2540]">
                  WhatsApp
                </h3>
                <p className="mb-3 mt-0.5 text-xs text-slate-500">
                  Inbound &amp; outbound messages via MyOperator. Free-form replies allowed within the 24-hour window after a lead messages you.
                </p>
                <WhatsAppConversation
                  leadId={selected.id}
                  leadName={selected.name}
                />
              </div>

              {/* Test report */}
              <div className="mt-8">
                <h3 className="font-display text-lg font-semibold text-[#0A2540]">Test Report</h3>
                {!report && <p className="mt-2 text-sm text-slate-500">Loading…</p>}
                {report && !report.session && (
                  <p className="mt-2 text-sm text-slate-500">Test not started yet.</p>
                )}
                {report?.session && (
                  <div className="mt-3 space-y-2">
                    {report.questions.map((q, i) => (
                      <div key={q.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              Q{i + 1} &middot; {q.subject}
                            </div>
                            <div className="mt-1 font-medium text-[#0A2540]">{q.question}</div>
                          </div>
                          <Badge
                            className={
                              q.is_correct
                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                                : q.chosen_index == null
                                  ? "bg-slate-100 text-slate-700 hover:bg-slate-100"
                                  : "bg-rose-100 text-rose-800 hover:bg-rose-100"
                            }
                          >
                            {q.is_correct ? "Correct" : q.chosen_index == null ? "Skipped" : "Wrong"}
                          </Badge>
                        </div>
                        <div className="mt-2 grid gap-1.5">
                          {q.options.map((opt, idx) => {
                            const isCorrect = idx === q.correct_index;
                            const isChosen = idx === q.chosen_index;
                            return (
                              <div
                                key={idx}
                                className={`rounded px-2.5 py-1.5 text-xs ${
                                  isCorrect
                                    ? "bg-emerald-50 text-emerald-800"
                                    : isChosen
                                      ? "bg-rose-50 text-rose-800"
                                      : "text-slate-600"
                                }`}
                              >
                                <span className="mr-2 font-mono-clock">
                                  {String.fromCharCode(65 + idx)}.
                                </span>
                                {opt}
                                {isCorrect && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider">correct</span>
                                )}
                                {isChosen && !isCorrect && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider">chosen</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Schedule call dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Schedule counsellor call</DialogTitle>
            <DialogDescription>
              {selected ? `${selected.name} · ${selected.phone}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1.5 h-11"
                  data-testid="schedule-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Time</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="mt-1.5 h-11"
                  data-testid="schedule-time"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Note (optional)</Label>
              <Textarea
                rows={3}
                placeholder="Talking points, language preference, etc."
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                className="mt-1.5"
                data-testid="schedule-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button
              onClick={submitSchedule}
              disabled={scheduleSaving}
              className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="schedule-confirm"
            >
              {scheduleSaving ? "Saving…" : "Confirm schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp dialog */}
      <Dialog open={whatsappOpen} onOpenChange={setWhatsappOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 tracking-tight">
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
              {whatsappData?.nudge ? "Send WhatsApp nudge" : "Send result on WhatsApp"}
            </DialogTitle>
            <DialogDescription>
              {selected
                ? `${selected.name} · +${whatsappData?.phone || selected.phone}`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {whatsappData && (
            <div className="space-y-4 py-2 min-w-0">
              <div className="min-w-0">
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Message preview
                </Label>
                <pre
                  className="mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-md border border-slate-200 bg-[#F8F9FA] p-3 text-xs leading-relaxed text-slate-700"
                  data-testid="whatsapp-message-preview"
                >
                  {whatsappData.message}
                </pre>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Click <strong>Open WhatsApp</strong> to launch a chat
                pre-filled with this message and the secure PDF link (valid 7
                days). Hit <em>Send</em> in WhatsApp to deliver to the student.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappOpen(false)}>
              Close
            </Button>
            <a
              href={whatsappData?.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                toast.success("WhatsApp opened — hit send to dispatch");
              }}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1EBE5C]"
              data-testid="whatsapp-open"
            >
              <MessageCircle className="h-4 w-4" />
              Open WhatsApp
              <ExternalLink className="h-3.5 w-3.5 opacity-80" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Team management dialog (admin only) */}
      <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 tracking-tight">
              <Users className="h-5 w-5 text-[#0A2540]" />
              Counsellor team
            </DialogTitle>
            <DialogDescription>
              Add new counsellors or deactivate inactive ones. They log in via
              the same admin URL and access leads + WhatsApp tools.
            </DialogDescription>
          </DialogHeader>

          {/* Add new team member */}
          <div className="rounded-lg border border-slate-200 bg-[#F8F9FA] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
              Add new team member
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <Input
                placeholder="Name"
                value={newCounsellor.name}
                onChange={(e) => setNewCounsellor({ ...newCounsellor, name: e.target.value })}
                className="h-10"
                data-testid="new-counsellor-name"
              />
              <Input
                type="email"
                placeholder="Email"
                value={newCounsellor.email}
                onChange={(e) => setNewCounsellor({ ...newCounsellor, email: e.target.value })}
                className="h-10"
                data-testid="new-counsellor-email"
              />
              <Input
                type="password"
                placeholder="Password (≥8)"
                value={newCounsellor.password}
                onChange={(e) => setNewCounsellor({ ...newCounsellor, password: e.target.value })}
                className="h-10"
                data-testid="new-counsellor-password"
              />
            </div>

            {/* Role + scope */}
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Role</Label>
                <Select value={newCounsellor.access_role} onValueChange={applyRolePreset}>
                  <SelectTrigger className="mt-0.5 h-10 text-sm" data-testid="new-counsellor-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_PRESETS.map((r) => (
                      <SelectItem key={r.key} value={r.key} className="text-sm">{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Can see (lead scope)</Label>
                <Select value={newCounsellor.lead_scope} onValueChange={(v) => setNewCounsellor((c) => ({ ...c, lead_scope: v }))}>
                  <SelectTrigger className="mt-0.5 h-10 text-sm" data-testid="new-counsellor-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_SCOPES.map((s) => (
                      <SelectItem key={s.key} value={s.key} className="text-sm">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              {(ROLE_PRESETS.find((r) => r.key === newCounsellor.access_role) || {}).desc}
            </p>

            {newCounsellor.lead_scope === "pipeline" && (
              <div className="mt-2">
                <Label className="text-[10px] uppercase tracking-wide text-slate-500">Restrict to pipeline</Label>
                <Select value={newCounsellor.pipeline_restrict} onValueChange={(v) => setNewCounsellor((c) => ({ ...c, pipeline_restrict: v }))}>
                  <SelectTrigger className="mt-0.5 h-10 text-sm" data-testid="new-counsellor-pipeline">
                    <SelectValue placeholder="Select pipeline" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamPipelines.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-sm">{p.label || p.name || p.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Permissions — editable for custom, read-only preview for presets */}
            <div className="mt-3">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">
                Can do (permissions){newCounsellor.access_role !== "custom" ? " — preset" : ""}
              </Label>
              <div className="mt-1.5 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {RBAC_PERMISSIONS.map((perm) => {
                  const checked = newCounsellor.permissions.includes(perm.key);
                  const editable = newCounsellor.access_role === "custom";
                  return (
                    <label
                      key={perm.key}
                      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${checked ? "border-[#0A2540]/30 bg-white" : "border-slate-200 bg-white/50"} ${editable ? "cursor-pointer" : "opacity-80"}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={!editable}
                        onCheckedChange={() => editable && toggleNewPerm(perm.key)}
                        data-testid={`new-counsellor-perm-${perm.key}`}
                      />
                      <span className="text-slate-700">{perm.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <Button
              size="sm"
              onClick={createCounsellor}
              disabled={creatingCounsellor}
              className="mt-3 gap-1.5 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="create-counsellor"
            >
              <UserPlus className="h-3.5 w-3.5" />
              {creatingCounsellor ? "Adding…" : "Add team member"}
            </Button>
          </div>

          {/* Existing list */}
          <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-slate-200">
            {counsellors.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 last:border-b-0"
                data-testid={`counsellor-row-${c.id}`}
              >
                <div>
                  <div className="text-sm font-semibold text-[#0A2540]">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.email}
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-[#C9A961]">
                      · {roleLabel(c.access_role || c.role)}
                    </span>
                    {!c.is_active && (
                      <Badge className="ml-2 bg-rose-100 text-rose-800 hover:bg-rose-100">
                        inactive
                      </Badge>
                    )}
                  </div>
                </div>
                {c.role !== "super_admin" && c.id !== user?.id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleCounsellor(c)}
                    className="h-8"
                    data-testid={`toggle-counsellor-${c.id}`}
                  >
                    {c.is_active ? "Deactivate" : "Activate"}
                  </Button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTeamOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 9: lead operations + tasks */}
      <AddLeadDialog
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        onCreated={() => loadAll()}
      />
      {/* Mobile-only floating action button */}
      <div className="md:hidden">
        <MobileFAB onAddLead={() => setAddLeadOpen(true)} />
      </div>

      {/* Edit-lead-details modal — mounts when user clicks the pencil in the drawer header */}
      {selected && (
        <EditLeadDetailsModal
          lead={selected}
          open={editDetailsOpen}
          onClose={() => setEditDetailsOpen(false)}
          onSaved={(updated) => {
            setSelected(updated);
            setLeads((arr) =>
              arr.map((x) => (x.id === updated.id ? updated : x)),
            );
          }}
        />
      )}
      <TasksPanel
        open={tasksOpen}
        onOpenChange={setTasksOpen}
        leads={leads}
      />
      <Sheet open={slaOpen} onOpenChange={setSlaOpen}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-xl"
          data-testid="sla-drawer"
        >
          <SheetHeader>
            <SheetTitle className="font-display flex items-center gap-2 tracking-tight">
              <Crown className="h-4 w-4 text-[#C9A961]" />
              SLA & Compliance
            </SheetTitle>
            <SheetDescription>
              Live SLA against the cadence rule for each lead&apos;s temperature.
            </SheetDescription>
          </SheetHeader>
          {slaOpen && <SLAComplianceSheet />}
        </SheetContent>
      </Sheet>

      <DispositionModal
        lead={dispoLead}
        onClose={() => setDispoLead(null)}
        onLogged={(updated) => applyLeadUpdate(updated)}
      />

      {/* Archive lead confirmation dialog */}
      <Dialog open={archiveOpen} onOpenChange={(o) => !archiveSubmitting && setArchiveOpen(o)}>
        <DialogContent className="max-w-md" data-testid="archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight text-[#0A2540]">
              Archive this lead?
            </DialogTitle>
            <DialogDescription>
              Archived leads are hidden from the pipeline, cadence pauses, and
              SLA stops. All notes & call history stay intact and you can
              restore anytime.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <div className="font-medium text-[#0A2540]">{selected.name}</div>
              <div className="text-xs text-slate-500">
                {selected.email} · {selected.phone}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">
              Reason (required) — kept in audit log
            </Label>
            <Textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. duplicate of #aabbcc, spam submission, student requested removal"
              rows={3}
              className="mt-1 resize-none"
              data-testid="archive-reason"
              maxLength={500}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setArchiveOpen(false)}
              disabled={archiveSubmitting}
              data-testid="archive-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitArchive}
              disabled={archiveSubmitting || !archiveReason.trim()}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="archive-confirm"
            >
              {archiveSubmitting ? "Archiving…" : "Archive lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="font-display mt-2 text-3xl font-bold text-[#0A2540]">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </div>
  );
}

function ConnectRateCard({ data }) {
  const pct = data?.today_pct ?? 0;
  const delta = data?.delta_vs_yesterday ?? 0;
  const connected = data?.today_connected ?? 0;
  const total = data?.today_total ?? 0;
  const deltaTone =
    delta > 0
      ? "text-emerald-700"
      : delta < 0
        ? "text-rose-700"
        : "text-slate-500";
  const deltaPrefix = delta > 0 ? "▲" : delta < 0 ? "▼" : "•";
  const ringTone = pct >= 30 ? "border-emerald-200 bg-emerald-50/40" : pct >= 15 ? "border-amber-200 bg-amber-50/40" : "border-rose-200 bg-rose-50/40";
  return (
    <div
      className={`rounded-xl border p-5 transition-shadow hover:shadow-sm ${ringTone}`}
      data-testid="phase-a-connect-rate"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-600">
          Connect rate · today
        </div>
        <PhoneCall className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-3xl font-bold text-[#0A2540]">
          {pct}%
        </div>
        {total > 0 && (
          <div className={`text-xs font-medium ${deltaTone}`}>
            {deltaPrefix} {Math.abs(delta)}% vs yday
          </div>
        )}
      </div>
      <div className="mt-1 text-xs text-slate-600">
        {total > 0 ? (
          <>
            <span className="font-medium text-emerald-700">{connected} connected</span>{" "}
            · {total} call{total === 1 ? "" : "s"} attempted
          </>
        ) : (
          "No calls logged today yet"
        )}
      </div>
    </div>
  );
}

function OverduePipelineCard({ data, onOpenLead, onApplyFilter, onResolved }) {
  const count = data?.count ?? 0;
  const hot = data?.hot_count ?? 0;
  const samples = data?.samples || [];
  const isCalm = count === 0;
  const [resolvingId, setResolvingId] = useState(null);

  async function resolveBreach(sample, channel) {
    setResolvingId(`${sample.id}-${channel}`);
    try {
      await api.post(`/admin/leads/${sample.id}/attempts`, {
        channel,
        source: "overdue_popover",
      });
      // Open the deep-link AFTER logging — keeps breach cleared even if user
      // doesn't actually click the system dialer.
      const phoneDigits = (sample.phone || "").replace(/\D/g, "");
      if (channel === "call" && phoneDigits) {
        window.open(`tel:${phoneDigits}`, "_self");
      } else if (channel === "whatsapp" && phoneDigits) {
        const greet = `Hi ${(sample.name || "").split(" ")[0] || ""}, this is your MedAdmits counsellor — checking in on your MBBS Abroad plans.`;
        window.open(
          `https://wa.me/${phoneDigits}?text=${encodeURIComponent(greet)}`,
          "_blank",
        );
      }
      toast.success(
        `Logged ${channel === "whatsapp" ? "WhatsApp" : "call"} attempt · 60-min cooldown started`,
      );
      onResolved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResolvingId(null);
    }
  }

  const tempStyle = {
    Hot: "bg-rose-100 text-rose-700 border-rose-200",
    Warm: "bg-amber-100 text-amber-700 border-amber-200",
    Cool: "bg-sky-100 text-sky-700 border-sky-200",
    Cold: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const sevDot = {
    high: "bg-rose-500",
    med: "bg-amber-500",
    low: "bg-slate-400",
  };
  const fmtHrs = (h) => {
    if (!h && h !== 0) return "—";
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 24) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  };

  const card = (
    <div
      className={`group flex w-full items-start rounded-xl border p-5 text-left transition-shadow hover:-translate-y-0.5 hover:shadow-sm ${
        isCalm
          ? "border-emerald-200 bg-emerald-50/40"
          : "border-rose-200 bg-rose-50/40 cursor-pointer"
      }`}
      data-testid="phase-a-overdue"
      onClick={isCalm ? undefined : onApplyFilter}
      role={isCalm ? undefined : "button"}
      tabIndex={isCalm ? -1 : 0}
      onKeyDown={(e) => {
        if (!isCalm && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onApplyFilter?.();
        }
      }}
    >
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-600">
            Overdue now
          </div>
          {isCalm ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <TriangleAlert className="h-4 w-4 text-rose-500" />
          )}
        </div>
        <div className="font-display mt-2 text-3xl font-bold text-[#0A2540]">
          {count}
        </div>
        <div className="mt-1 text-xs text-slate-600">
          {isCalm ? (
            <span className="text-emerald-700">All leads are inside SLA</span>
          ) : (
            <>
              <span className="font-medium text-rose-700">{count} behind</span>
              {hot > 0 && <span> · {hot} 🔥 Hot</span>}
              <span className="ml-1 text-slate-400 group-hover:underline">
                Open queue →
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (isCalm || samples.length === 0) {
    return card;
  }

  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>{card}</HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="w-[360px] p-0 overflow-hidden"
        data-testid="overdue-popover"
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-rose-50/40">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-600">
            Top breaches
          </div>
          <div className="text-sm font-semibold text-[#0A2540] mt-0.5">
            {count} lead{count > 1 ? "s" : ""} behind SLA · {hot} Hot
          </div>
        </div>
        <ul className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
          {samples.map((s) => {
            const phoneDigits = (s.phone || "").replace(/\D/g, "");
            const canCall = Boolean(phoneDigits);
            return (
              <li key={s.id}>
                <div className="px-4 py-3 hover:bg-slate-50 focus-within:bg-slate-50 transition">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenLead?.(s.id);
                    }}
                    data-testid={`overdue-sample-${s.id}`}
                    className="w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              sevDot[s.severity] || "bg-slate-400"
                            }`}
                          />
                          <span className="font-medium text-sm text-[#0A2540] truncate">
                            {s.name}
                          </span>
                        </div>
                        <div className="text-[11.5px] text-slate-600 mt-1 line-clamp-1">
                          {s.reason}
                        </div>
                        {s.assigned_to_name && (
                          <div className="text-[10.5px] text-slate-400 mt-0.5">
                            Owner: {s.assigned_to_name}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <span
                          className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
                            tempStyle[s.lead_temperature] || tempStyle.Cold
                          }`}
                        >
                          {s.lead_temperature}
                        </span>
                        <div className="text-[10.5px] text-rose-700 font-semibold mt-1 tabular-nums">
                          {fmtHrs(s.hours_behind)} behind
                        </div>
                      </div>
                    </div>
                  </button>
                  {/* Quick-action resolve row */}
                  <div className="mt-2 flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={!canCall || resolvingId === `${s.id}-call`}
                      onClick={(e) => {
                        e.stopPropagation();
                        resolveBreach(s, "call");
                      }}
                      data-testid={`resolve-call-${s.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10.5px] font-semibold text-[#0A2540] hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={canCall ? "Log call attempt & dial" : "No phone number"}
                    >
                      <PhoneCall className="h-3 w-3" />
                      Call
                    </button>
                    <button
                      type="button"
                      disabled={!canCall || resolvingId === `${s.id}-whatsapp`}
                      onClick={(e) => {
                        e.stopPropagation();
                        resolveBreach(s, "whatsapp");
                      }}
                      data-testid={`resolve-wa-${s.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10.5px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={canCall ? "Log WhatsApp attempt & open chat" : "No phone number"}
                    >
                      <MessageCircle className="h-3 w-3" />
                      WhatsApp
                    </button>
                    <span className="text-[10px] text-slate-400 ml-1">
                      Logs attempt · 60-min cooldown
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onApplyFilter?.();
          }}
          data-testid="overdue-popover-open-queue"
          className="block w-full px-4 py-2.5 text-center text-[12px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 transition border-t border-rose-100"
        >
          Open queue ({count}) →
        </button>
      </HoverCardContent>
    </HoverCard>
  );
}

function PersonalLinksCard({ data }) {
  const total = data?.week_total ?? 0;
  const board = data?.leaderboard || [];
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50/40 p-5 transition-shadow hover:shadow-sm"
      data-testid="phase-a-personal-links"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-600">
          From personal links · 7d
        </div>
        <LinkIcon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="font-display mt-2 text-3xl font-bold text-[#0A2540]">
        {total}
      </div>
      <div className="mt-1.5 space-y-0.5 text-xs">
        {board.length === 0 ? (
          <div className="text-slate-500">
            Share counsellor links from Settings → Share form to attribute leads.
          </div>
        ) : (
          board.map((row, i) => (
            <div
              key={row.user_id || i}
              className="flex items-center justify-between text-slate-700"
              data-testid={`personal-link-row-${i}`}
            >
              <span className="truncate">
                <span className="mr-1">{medals[i] || "·"}</span>
                {row.name}
              </span>
              <span className="font-mono-clock font-medium text-[#0A2540]">
                {row.count}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-[#0A2540]">{value || "—"}</div>
    </div>
  );
}

function formatQualification(q) {
  if (!q) return null;
  const map = {
    "10th": "10th Standard",
    "12th": "12th Standard / Intermediate",
    bachelors: "Bachelor's degree",
    masters: "Master's degree",
  };
  return map[q] || q;
}

function formatCallTime(t) {
  if (!t) return null;
  const map = {
    morning: "Morning (9 AM – 12 PM)",
    afternoon: "Afternoon (12 PM – 4 PM)",
    evening: "Evening (4 PM – 8 PM)",
  };
  return map[t] || t;
}
